// `usePublicShareHandler` — issues a public-share token for the
// property, then opens the native share sheet (or copies the URL).
// Used by the desktop action-grid tile and the mobile More sheet
// row so both surfaces share one implementation.

import { useCallback, useState } from "react";
import { toast } from "sonner";

import { localizeError } from "@/lib/errorMessages";
import { shareOrCopy } from "@/lib/share";
import { useIssueShareToken } from "@/features/property/api";
import type { Property } from "@/types/property";

export function usePublicShareHandler(property: Property) {
  const issueMutation = useIssueShareToken(property.id);
  const [busy, setBusy] = useState(false);

  const trigger = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    try {
      const result = await issueMutation.mutateAsync({});
      if (!result.token) {
        // Defensive: the backend always returns a fresh raw token now, but
        // never build a `/public/p/null` URL if that ever regresses.
        toast.error("Không thể tạo liên kết chia sẻ. Vui lòng thử lại.");
        return;
      }
      const origin = typeof window !== "undefined" ? window.location.origin : "";
      const url = `${origin}/public/p/${result.token}`;
      const outcome = await shareOrCopy({
        title: property.buildingName ?? "Bất động sản",
        text: property.buildingName ?? "Bất động sản",
        url,
      });
      switch (outcome.kind) {
        case "shared":
          toast.success("Đã mở hộp thoại chia sẻ");
          break;
        case "copied":
          toast.success("Đã sao chép liên kết");
          break;
        case "cancelled":
          break;
        case "error":
          toast.error("Không thể chia sẻ. Vui lòng thử lại.");
          break;
      }
    } catch (error) {
      toast.error(localizeError(error));
    } finally {
      setBusy(false);
    }
  }, [busy, issueMutation, property.buildingName]);

  return { busy, trigger };
}
